import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client.ts";
import { toast } from "../components/feedback.tsx";
import { Card, Empty, TableSkeleton } from "../components/ui.tsx";

interface ReplyRow {
  id: string;
  from_header: string | null;
  from_address: string;
  to_address: string;
  subject: string | null;
  received_at: string;
  is_auto_reply: boolean;
  match_strategy: string | null;
  snippet: string | null;
  contact_id: string | null;
  contact_email: string | null;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  campaign_type: "broadcast" | "sequence" | null;
  handled_at: string | null;
  handled_by: string | null;
  pulse_lead_id: number | null;
  pulse_stage: string | null;
  pulse_status: string | null;
  pulse_url: string | null;
}

interface ReplyDetail extends ReplyRow {
  plain_body: string | null;
  html_body: string | null;
}

const FILTERS = [
  ["unhandled", "To handle"],
  ["replies", "All replies"],
  ["unmatched", "Not linked to a campaign"],
  ["all", "Everything, incl. auto-replies"],
] as const;
type Filter = (typeof FILTERS)[number][0];
const PAGE = 50;

/** Who a reply is from: their contact name if we know them, else the From header. */
function sender(r: ReplyRow): string {
  const name = [r.first_name, r.last_name].filter(Boolean).join(" ");
  if (name) return name;
  return (
    r.from_header
      ?.replace(/<[^>]+>/, "")
      .replace(/"/g, "")
      .trim() || r.from_address
  );
}

export function RepliesPage() {
  const [filter, setFilter] = useState<Filter>("unhandled");
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["replies", filter, offset],
    queryFn: () =>
      api.get<{ total: number; rows: ReplyRow[] }>(
        `/inbox?filter=${filter}&limit=${PAGE}&offset=${offset}`,
      ),
    refetchInterval: 30_000,
  });
  const total = list.data?.total ?? 0;

  return (
    <div className="page">
      <div className="page-header">
        <h1>Replies</h1>
        <p>
          What people wrote back. Each reply is linked to the campaign it answers, and counts as a
          reply on that campaign&rsquo;s report.
        </p>
      </div>

      <div className="toolbar">
        <div className="tabs">
          {FILTERS.map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={value === filter ? "active" : ""}
              style={{ textTransform: "none" }}
              onClick={() => {
                setFilter(value);
                setOffset(0);
                setOpen(null);
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <Card>
        {list.isLoading ? (
          <TableSkeleton />
        ) : list.data?.rows.length === 0 ? (
          <Empty>
            {filter === "unhandled"
              ? "Nothing waiting. Every reply has been dealt with."
              : filter === "replies"
                ? "No replies yet. Replies arrive through the inbound route set up for each Postal installation."
                : "Nothing here."}
          </Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>From</th>
                <th>Message</th>
                <th>Campaign</th>
                <th>Received</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.data?.rows.map((r) => (
                <ReplyRowView
                  key={r.id}
                  reply={r}
                  open={open === r.id}
                  onToggle={() => setOpen(open === r.id ? null : r.id)}
                />
              ))}
            </tbody>
          </table>
        )}

        {total > PAGE && (
          <div className="card-body pager">
            <span>
              {offset + 1}&ndash;{Math.min(offset + PAGE, total)} of {total.toLocaleString()}
            </span>
            <button
              type="button"
              className="btn ghost small"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(offset - PAGE, 0))}
            >
              Previous
            </button>
            <button
              type="button"
              className="btn ghost small"
              disabled={offset + PAGE >= total}
              onClick={() => setOffset(offset + PAGE)}
            >
              Next
            </button>
          </div>
        )}
      </Card>
    </div>
  );
}

function ReplyRowView({
  reply: r,
  open,
  onToggle,
}: {
  reply: ReplyRow;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr className={`reply-row${open ? " open" : ""}`}>
        <td>
          <button type="button" className="row-button" onClick={onToggle} aria-expanded={open}>
            <div style={{ fontWeight: 600 }}>{sender(r)}</div>
            <div className="mono" style={{ color: "var(--gray-500)", fontSize: 11 }}>
              {r.contact_email ?? r.from_address}
            </div>
          </button>
        </td>
        <td>
          <button type="button" className="row-button" onClick={onToggle} tabIndex={-1}>
            <div style={{ fontWeight: 600 }}>
              {r.subject || <em>(no subject)</em>}
              {r.is_auto_reply && (
                <span className="pill mute" style={{ marginLeft: 6 }}>
                  auto-reply
                </span>
              )}
            </div>
            <div className="snippet">{r.snippet}</div>
          </button>
        </td>
        <td>
          {r.campaign_id ? (
            <Link to={`/campaigns/${r.campaign_id}`}>{r.campaign_name}</Link>
          ) : (
            <span style={{ color: "var(--gray-500)" }}>—</span>
          )}
        </td>
        <td className="mono">{new Date(r.received_at).toLocaleString()}</td>
        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
          <HandledButton reply={r} />
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5} style={{ background: "var(--gray-50)" }}>
            <ReplyBody id={r.id} />
          </td>
        </tr>
      )}
    </>
  );
}

function ReplyBody({ id }: { id: string }) {
  const detail = useQuery({
    queryKey: ["reply", id],
    queryFn: () => api.get<ReplyDetail>(`/inbox/${id}`),
  });
  const d = detail.data;
  if (!d) return null;

  return (
    <div className="reply-detail">
      <div className="reply-links">
        {d.contact_id && (
          <Link className="btn ghost small" to={`/contacts?contact=${d.contact_id}`}>
            Open contact
          </Link>
        )}
        {d.pulse_url && (
          <a className="btn ghost small" href={d.pulse_url} target="_blank" rel="noreferrer">
            Open lead #{d.pulse_lead_id} in Pulse ↗
          </a>
        )}
        {d.pulse_stage && (
          <span className="hint">
            Pulse: {d.pulse_stage.replaceAll("_", " ")}
            {d.pulse_status ? ` · sub-status ${d.pulse_status}` : ""}
          </span>
        )}
        {d.handled_at && (
          <span className="hint">
            Handled {new Date(d.handled_at).toLocaleString()}
            {d.handled_by ? ` by ${d.handled_by}` : ""}
          </span>
        )}
      </div>
      <div className="reply-meta">
        <span>
          <strong>From</strong> {d.from_header ?? d.from_address}
        </span>
        <span>
          <strong>To</strong> {d.to_address}
        </span>
        {d.company && (
          <span>
            <strong>Company</strong> {d.company}
          </span>
        )}
      </div>
      {d.plain_body ? (
        <pre className="reply-body">{d.plain_body}</pre>
      ) : d.html_body ? (
        // Someone else's HTML: an empty sandbox runs no script and cannot reach
        // this page, so a hostile reply can do no more than look odd.
        <iframe title="Reply" className="reply-html" sandbox="" srcDoc={d.html_body} />
      ) : (
        <p style={{ color: "var(--gray-500)" }}>
          This reply arrived before message text was being kept, so only its subject is on file.
          Postal still has the full message.
        </p>
      )}
    </div>
  );
}

/**
 * Dealt with, or not. Marking one done takes it off "To handle"; the row
 * stays put until the list refreshes, so a slip of the mouse can be undone
 * from the same place.
 */
function HandledButton({ reply }: { reply: ReplyRow }) {
  const qc = useQueryClient();
  const set = useMutation({
    mutationFn: (handled: boolean) => api.post(`/inbox/${reply.id}/handled`, { handled }),
    onSuccess: (_, handled) => {
      toast(handled ? "Marked handled" : "Back in To handle");
      void qc.invalidateQueries({ queryKey: ["replies"] });
      void qc.invalidateQueries({ queryKey: ["reply", reply.id] });
    },
    onError: (err: Error) => toast(err.message, "bad"),
  });
  const handled = Boolean(reply.handled_at);
  return (
    <button
      type="button"
      className={`btn ${handled ? "ghost" : ""} small`}
      disabled={set.isPending}
      title={handled && reply.handled_by ? `Handled by ${reply.handled_by}` : undefined}
      onClick={() => set.mutate(!handled)}
    >
      {handled ? "Reopen" : "Done ✓"}
    </button>
  );
}
